import React from 'react';
import { ConfiguratorState } from '../../types';
import { formatDisplayPrice, taxNote } from '../../utils/priceFormat';

/**
 * PriceFooterBar — persistente Preis- und Navigationsleiste.
 *
 * Auf jeder Seite: Zwischenpreise (Positionen), laufender Preis
 * in der oben gewählten Netto-/Brutto-Darstellung, Schritt-Kontext
 * und Zurück/Weiter.
 */

export type FooterPriceLine = {
  id: string;
  label: string;
  net: number;
};

interface Props {
  state: ConfiguratorState;
  lines?: FooterPriceLine[];
  showGross: boolean;
  stepNumber: number;
  stepCount: number;
  currentStepName: string;
  nextStepName?: string;
  nextLabel?: string;
  backLabel?: string;
  onNext?: () => void;
  onBack?: () => void;
  nextDisabled?: boolean;
  hideNext?: boolean;
  hideBack?: boolean;
  nextRef?: React.Ref<HTMLButtonElement>;
  priceStatus?: 'idle' | 'loading' | 'ready' | 'error';
  priceError?: string | null;
  onRetryPrice?: () => void;
  startingPrice?: number;
}

export default function PriceFooterBar({
  state,
  lines = [],
  showGross,
  stepNumber,
  stepCount,
  currentStepName,
  nextStepName,
  nextLabel = 'Weiter',
  backLabel = 'Zurück',
  onNext,
  onBack,
  nextDisabled = false,
  hideNext = false,
  hideBack = false,
  nextRef,
  priceStatus = 'idle',
  priceError = null,
  onRetryPrice,
  startingPrice,
}: Props) {
  const hasPrice = state.totalPrice > 0;
  const productShort = state.product?.replace('SkyVision ', '') || '—';
  const pricedLines = lines.filter((line) => line.net > 0);

  return (
    <div className="sv-pricebar" role="contentinfo" aria-label="Preis und Navigation">
      {pricedLines.length > 0 && (
        <ul className="sv-pricebar-lines" aria-label="Zwischenpreise">
          {pricedLines.map((line) => (
            <li key={line.id} className="sv-pricebar-line">
              <span className="sv-pricebar-line-label">{line.label}</span>
              <span className="sv-pricebar-line-value">
                {formatDisplayPrice(line.net, showGross)}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="sv-pricebar-inner">
        <div className="sv-pricebar-context">
          {stepNumber > 0 && (
            <>
              <span className="sv-pricebar-step">
                Schritt <span className="sv-pricebar-step-num">{stepNumber}</span> / {stepCount}
              </span>
              <span className="sv-pricebar-sep" aria-hidden>·</span>
            </>
          )}
          <span className="sv-pricebar-stepname">{currentStepName}</span>
          {state.product && (
            <>
              <span className="sv-pricebar-sep" aria-hidden>·</span>
              <span className="sv-pricebar-product">SkyVision {productShort}</span>
            </>
          )}
        </div>

        <div className="sv-pricebar-price">
          {priceStatus === 'error' ? (
            <span className="sv-pricebar-price-error" role="alert">
              {priceError || 'Preis konnte nicht geladen werden.'}
              {onRetryPrice && (
                <button type="button" className="sv-pricebar-retry" onClick={onRetryPrice}>
                  Erneut laden
                </button>
              )}
            </span>
          ) : priceStatus === 'loading' && !hasPrice ? (
            <span className="sv-pricebar-price-placeholder">Preis wird geladen …</span>
          ) : hasPrice ? (
            <>
              <span className="sv-pricebar-price-label">Konfigurationspreis</span>
              <span className="sv-pricebar-price-value">
                {formatDisplayPrice(state.totalPrice, showGross)}
              </span>
              <span className="sv-pricebar-price-note">{taxNote(showGross)}</span>
            </>
          ) : startingPrice ? (
            <>
              <span className="sv-pricebar-price-label">ab</span>
              <span className="sv-pricebar-price-value">
                {formatDisplayPrice(startingPrice, showGross, 0)}
              </span>
              <span className="sv-pricebar-price-note">{taxNote(showGross)}</span>
            </>
          ) : (
            <span className="sv-pricebar-price-placeholder">
              Ihr Preis entsteht mit jeder Auswahl
            </span>
          )}
        </div>

        <div className="sv-pricebar-nav">
          {nextStepName && !hideNext && (
            <span className="sv-pricebar-next-hint" aria-hidden>
              Weiter: {nextStepName}
            </span>
          )}
          {!hideBack && onBack && (
            <button
              type="button"
              onClick={onBack}
              className="sv-btn sv-btn-ghost"
            >
              <span aria-hidden>←</span>
              <span>{backLabel}</span>
            </button>
          )}
          {!hideNext && onNext && (
            <button
              type="button"
              ref={nextRef}
              onClick={onNext}
              disabled={nextDisabled}
              className={`sv-btn sv-btn-primary ${nextDisabled ? 'is-disabled' : ''}`}
              title={nextLabel}
            >
              <span className="sv-btn-label">{nextLabel}</span>
              <span aria-hidden>→</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
